import { type PropsWithChildren } from "react";

import { Tooltip } from "#components/v2/Tooltip";
import { useCopyToClipboard } from "#hooks";
import { CopyIcon } from "#icons";
import { customTv } from "#utils";

export interface CopyButtonProps extends PropsWithChildren {
  /** Overrides the default aria-label (`Copy ${copyLabel} button` or `Copy value button`). */
  "aria-label"?: string;
  className?: string;
  copyLabel?: string;
  /**
   * Controls where the built-in copy-status tooltip is attached.
   * - `container` (default): tooltip appears when hovering anywhere on the copy button row
   * - `icon`: tooltip appears only when hovering the copy icon
   * - `none`: disables the built-in tooltip
   */
  tooltipTarget?: "container" | "icon" | "none";
  value: string;
  variant?: "stacklet-orange"; // color variants
}

export function CopyButton({
  "aria-label": ariaLabel,
  children,
  className,
  copyLabel,
  tooltipTarget = "container",
  value,
  variant,
}: CopyButtonProps) {
  const [copyValueStatus, copyValue] = useCopyToClipboard(value);

  let status = `Copy ${copyLabel ?? "value"}`;
  if (copyValueStatus === "copied") {
    status = "Copied";
  } else if (copyValueStatus === "failed") {
    status = "Copy failed!";
  }

  const copyButtonStyles = customTv({
    base: "flex items-center gap-2-5 w-fit hover:cursor-pointer",
    variants: {
      variant: { "stacklet-orange": "text-stacklet-orange-700" },
    },
  });

  const buttonEl = (
    <div
      aria-label={ariaLabel ?? `Copy ${copyLabel ?? "value"} button`}
      className={copyButtonStyles({ class: className, variant })}
      onClick={(event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        copyValue();
      }}
      role="button"
    >
      {children}
      {tooltipTarget === "icon" ? (
        <Tooltip title={status} asChild>
          <span className="inline-flex shrink-0">
            <CopyIcon />
          </span>
        </Tooltip>
      ) : (
        <span className="inline-flex shrink-0">
          <CopyIcon />
        </span>
      )}
    </div>
  );

  if (tooltipTarget === "none" || tooltipTarget === "icon") {
    return buttonEl;
  }

  return (
    <Tooltip title={status} asChild>
      {buttonEl}
    </Tooltip>
  );
}
